import React, { useEffect, useMemo, useState } from 'react';
import { BadgeDollarSign, TrendingDown, TrendingUp } from 'lucide-react';

export default function MerchantPriceInsights2() {
  const [merchantId, setMerchantId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [priceComp, setPriceComp] = useState([]);
  const [priceInsights, setPriceInsights] = useState([]);
  const [piLoading, setPiLoading] = useState(false);
  const [pcLoading, setPcLoading] = useState(false);
  const [piDebug, setPiDebug] = useState(null);

  const PAGE_SIZE = 25;
  const [pcPage, setPcPage] = useState(1);
  const [piPage, setPiPage] = useState(1);

  const getBrandId = () => {
    return (
      localStorage.getItem('selectedBrandId') ||
      localStorage.getItem('currentBrandId') ||
      sessionStorage.getItem('selectedBrandId') ||
      ''
    );
  };

  const isAbortErr = (e) => {
    if (!e) return false;
    const name = e.name || '';
    const msg = e.message || String(e) || '';
    return name === 'AbortError' || msg.includes('AbortError') || msg.includes('signal is aborted');
  };

  const fetchSafe = async (url, options) => {
    try {
      const r = await fetch(url, options);
      return r;
    } catch (e) {
      if (isAbortErr(e)) return null;
      throw e;
    }
  };

  const getQueryParam = (key, fallback = '') => {
    try {
      const v = new URLSearchParams(window.location.search).get(key);
      return (v == null || String(v).trim() === '') ? fallback : String(v);
    } catch (_) {
      return fallback;
    }
  };

  const fmtMoney = (val, currency) => {
    if (val == null || !Number.isFinite(Number(val))) return '-';
    const n = Number(val);
    const cur = currency || '';
    return `${n.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ${cur}`.trim();
  };

  // Resolve merchantId from MERCHANT_CENTER connection
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        if (alive) setLoading(true);
        if (alive) setError('');
        const brandId = getBrandId();
        if (!brandId) return;
        const headers = { 'Content-Type': 'application/json', 'X-Brand-ID': brandId };
        const c = await fetchSafe('https://api.glowytics.com/api/auth/connections', { method: 'GET', headers, credentials: 'include' });
        if (c && c.ok) {
          const connections = await c.json();
          const arr = Array.isArray(connections) ? connections : [];
          const found = arr.find(x => String(x?.platform || '').toUpperCase() === 'MERCHANT_CENTER') || null;
          const mid = String(found?.customerId || found?.merchantId || found?.accountId || found?.merchant_id || '').trim();
          if (alive) setMerchantId(mid);
          if (!mid && alive) setError('Merchant Center account not selected. Go to Settings → Merchant Center and choose an account.');
        }
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to resolve Merchant Center account.');
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, []);

  // Fetch Price Competitiveness (benchmark)
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        if (!merchantId) return;
        const brandId = getBrandId();
        const headers = { 'Content-Type': 'application/json', 'X-Brand-ID': brandId };
        const country = getQueryParam('country', 'TR').trim().toUpperCase();
        if (alive) setPcLoading(true);

        const qs = new URLSearchParams();
        qs.set('merchantId', merchantId);
        if (country) qs.set('country', country);
        qs.set('pageSize', '500');

        const r = await fetchSafe(`https://api.glowytics.com/api/auth/merchant-center/reports/price-competitiveness?${qs.toString()}`, {
          method: 'GET',
          headers,
          credentials: 'include'
        });
        if (!r) return;
        const j = await r.json();
        const rows = Array.isArray(j?.items) ? j.items : [];
        if (alive) setPriceComp(rows);
      } catch (_) {
        // ignore
      } finally {
        if (alive) setPcLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [merchantId]);

  // Fetch Price Insights (suggested discounted price)
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        if (!merchantId) return;
        const brandId = getBrandId();
        const headers = { 'Content-Type': 'application/json', 'X-Brand-ID': brandId };
        const country = getQueryParam('country', 'TR').trim().toUpperCase();
        if (alive) setPiLoading(true);

        const qs = new URLSearchParams();
        qs.set('merchantId', merchantId);
        if (country) qs.set('country', country);
        qs.set('pageSize', '500');
        qs.set('debug', '1');

        const r = await fetchSafe(`https://api.glowytics.com/api/auth/merchant-center/reports/price-insights?${qs.toString()}`, {
          method: 'GET',
          headers,
          credentials: 'include'
        });
        if (!r) return;
        const j = await r.json();
        const rows = Array.isArray(j?.items) ? j.items : [];
        if (alive) {
          setPriceInsights(rows);
          setPiDebug(j?.debug || null);
        }
      } catch (e) {
        // If the view isn't available for some accounts, show a helpful hint
        if (alive) setError(e?.message || 'Failed to load price insights.');
      } finally {
        if (alive) setPiLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [merchantId]);

  // Pagination helpers
  const makePager = (arr, page, setPage) => {
    const total = Math.max(1, Math.ceil((arr?.length || 0) / PAGE_SIZE));
    const current = Math.min(Math.max(1, page), total);
    const slice = (arr || []).slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
    const buttons = (() => {
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const out = new Set([1, total, current, current - 1, current + 1, current - 2, current + 2]);
      return Array.from(out).filter(n => n >= 1 && n <= total).sort((a, b) => a - b);
    })();
    const Pager = () => (
      total > 1 ? (
        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="text-[11px] text-gray-500">Page {current} / {total}</div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={current <= 1}
              className="px-2 py-1 text-[11px] rounded border bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Prev
            </button>
            {buttons.map((p, idx) => {
              const prev = buttons[idx - 1];
              const showDots = idx > 0 && prev != null && p - prev > 1;
              return (
                <React.Fragment key={p}>
                  {showDots ? <span className="px-1 text-[11px] text-gray-400">…</span> : null}
                  <button
                    onClick={() => setPage(p)}
                    className={`px-2 py-1 text-[11px] rounded border ${p === current ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:bg-gray-50'}`}
                  >
                    {p}
                  </button>
                </React.Fragment>
              );
            })}
            <button
              onClick={() => setPage(p => Math.min(total, p + 1))}
              disabled={current >= total}
              className="px-2 py-1 text-[11px] rounded border bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      ) : null
    );
    return { total, current, slice, Pager };
  };

  const pcPager = useMemo(() => makePager(priceComp, pcPage, setPcPage), [priceComp, pcPage]);
  const piPager = useMemo(() => makePager(priceInsights, piPage, setPiPage), [priceInsights, piPage]);

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <img src="/google-merchant-centerpngg.svg" alt="Merchant Center" className="h-6 w-auto" />
            <h1 className="text-xl font-semibold text-gray-900">Merchant Price Insights</h1>
          </div>
          <div className="text-sm text-gray-500">Live price competitiveness & discount price suggestions</div>
        </div>

        {loading && (
          <div className="mb-4 text-xs px-3 py-2 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
            Loading…
          </div>
        )}
        {!!error && (
          <div className="mb-4 text-xs px-3 py-2 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
            {error}
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Price Competitiveness */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <BadgeDollarSign className="w-4 h-4 text-indigo-600" />
                <span>Price comparison (benchmark)</span>
              </div>
              <div className="text-xs text-gray-500">{pcLoading ? 'loading…' : `${priceComp.length} items`}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[11px]">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-2 py-1 text-left">title</th>
                    <th className="px-2 py-1 text-right">your price</th>
                    <th className="px-2 py-1 text-right">benchmark</th>
                    <th className="px-2 py-1 text-right">delta</th>
                  </tr>
                </thead>
                <tbody>
                  {pcPager.slice.map((r, idx) => {
                    const delta = (r.priceDeltaPct == null ? null : Number(r.priceDeltaPct));
                    const isUp = delta != null && delta > 0;
                    const isDown = delta != null && delta < 0;
                    return (
                      <tr key={r.id || idx} className="border-t">
                        <td className="px-2 py-1 max-w-[360px] truncate" title={r.title || ''}>{r.title || '-'}</td>
                        <td className="px-2 py-1 text-right">{fmtMoney(r.price, r.priceCurrency)}</td>
                        <td className="px-2 py-1 text-right">{fmtMoney(r.benchmarkPrice, r.benchmarkCurrency || r.priceCurrency)}</td>
                        <td className="px-2 py-1 text-right">
                          {delta == null ? '-' : (
                            <span className={`inline-flex items-center gap-1 ${isUp ? 'text-rose-600' : isDown ? 'text-emerald-700' : 'text-gray-700'}`}>
                              {isUp ? <TrendingUp className="w-3 h-3" /> : isDown ? <TrendingDown className="w-3 h-3" /> : null}
                              {delta.toFixed(1)}%
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <pcPager.Pager />
          </div>

          {/* Price Insights */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <BadgeDollarSign className="w-4 h-4 text-indigo-600" />
                <span>Discount price suggestions</span>
              </div>
              <div className="text-xs text-gray-500">{piLoading ? 'loading…' : `${priceInsights.length} items`}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[11px]">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-2 py-1 text-left">title</th>
                    <th className="px-2 py-1 text-right">your price</th>
                    <th className="px-2 py-1 text-right">suggested</th>
                    <th className="px-2 py-1 text-right">click uplift</th>
                  </tr>
                </thead>
                <tbody>
                  {piPager.slice.map((r, idx) => (
                    <tr key={r.id || idx} className="border-t">
                      <td className="px-2 py-1 max-w-[360px] truncate" title={r.title || ''}>{r.title || '-'}</td>
                      <td className="px-2 py-1 text-right">{fmtMoney(r.currentPrice, r.currentCurrency)}</td>
                      <td className="px-2 py-1 text-right">{r.suggestedPrice != null ? fmtMoney(r.suggestedPrice, r.suggestedCurrency || r.currentCurrency) : '-'}</td>
                      <td className="px-2 py-1 text-right">
                        {r.predictedClicksChangePct != null
                          ? `${Number(r.predictedClicksChangePct).toFixed(1)}%`
                          : (r.predictedClicksChange != null ? String(r.predictedClicksChange) : '-')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <piPager.Pager />
            <div className="mt-2 text-[11px] text-gray-500">
              Note: Price suggestions availability depends on Merchant Center eligibility; some accounts may return empty.
            </div>
            {priceInsights.length === 0 && piDebug?.attempts?.length ? (
              <details className="mt-3">
                <summary className="cursor-pointer text-[11px] text-gray-600">Debug: API view probe results</summary>
                <div className="mt-2 text-[11px] text-gray-700 max-h-64 overflow-auto border rounded p-2 bg-gray-50">
                  <pre className="whitespace-pre-wrap">{JSON.stringify(piDebug, null, 2)}</pre>
                </div>
              </details>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}


